import ErrorMessage from '@/components/ErrorMessage'
import LinkAccountModal from '@/components/LinkAccount'
import Loading from '@/components/Loading'
import LoginSignup from '@/components/LoginSignup'
import OrgUserAdd from '@/components/OrgUserAdd'
import OrgUsersInvitationsList from '@/components/OrgUsersInvitationsList'
import OrgUsersList from '@/components/OrgUsersList'
import ResetPassword from '@/components/ResetPassword'
import { OrgUser } from '@/models/Users'
import { Unauthenticated, Authenticated } from '@/utils/auth/auth'
import { getCurrentUser } from '@/utils/auth/UserProvider'
import { useInvitations, useOrgUsers } from '@/utils/hooks/invitations'
import { getEffectiveOrgUuid } from '@/utils/utils'
import { Box, Button, Typography, useMediaQuery, useTheme } from '@mui/material'
import { useState } from 'react'
import { useFocusManagement } from '@/utils/hooks/useFocusManagement'
import { usePageTitle } from '@/utils/hooks/usePageTitle'

export default function OrgUsersPage() {
  usePageTitle('Organisation Users')

  const { user, token, isLoading: userLoading, orgs } = getCurrentUser()
  const [invitingUser, setInvitingUser] = useState(false)
  const focusRef = useFocusManagement()
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false)
  const [showLinkAccountModal, setShowLinkAccountModal] = useState(false)

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  if (!userLoading && orgs && orgs.length === 0) {
    window.location.href = '/'
  }

  const effectiveOrgUuid = getEffectiveOrgUuid(user, orgs)

  const { data: orgUsers, isLoading: orgUsersLoading } = useOrgUsers(
    token,
    effectiveOrgUuid
  )

  const { data: invitations, isLoading: invitationsLoading } = useInvitations(
    token,
    effectiveOrgUuid
  )

  if (userLoading || orgUsersLoading || invitationsLoading) {
    return <Loading />
  }

  return (
    <>
      <Unauthenticated>
        <LoginSignup />
      </Unauthenticated>
      <Authenticated>
        <>
          {!user || !token || (!effectiveOrgUuid && <ErrorMessage />)}
          {showResetPasswordModal && (
            <ResetPassword
              email={user?.email || ''}
              showResetPasswordModal={showResetPasswordModal}
              setShowResetPasswordModal={setShowResetPasswordModal}
              token={token}
            />
          )}
          {showLinkAccountModal && (
            <LinkAccountModal
              showLinkAccountModal={showLinkAccountModal}
              setShowLinkAccountModal={setShowLinkAccountModal}
              token={token}
            />
          )}

          <Box
            sx={{
              margin: '0 auto',
              marginTop: 5,
              marginBottom: 8,
              width: '100%',
              py: isMobile ? 2 : 5,
              px: isMobile ? 2 : 15,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                gap: isMobile ? 2 : 0,
              }}
            >
              <Typography
                variant="h1"
                component="h1"
                ref={focusRef}
                tabIndex={-1}
                sx={{
                  mb: 1,
                  textAlign: 'left',
                  fontSize: '34px',
                  '&:focus': {
                    outline: 'none',
                  },
                }}
              >
                Manage Organisation Users
              </Typography>

              <Button
                variant="contained"
                disableRipple
                sx={{
                  borderRadius: 2,
                  bgcolor: '#1C3045',
                  color: 'white',
                  mb: 1,
                  mx: 1,
                  px: 4,
                  fontSize: '16px',
                  textTransform: 'none',
                  boxShadow: 'none',
                }}
                onClick={() => setInvitingUser(true)}
              >
                Invite a User to Your Organisation
              </Button>
            </Box>
            <Button
              variant="text"
              disableRipple
              sx={{
                textTransform: 'none',
                '&:focus': {
                  backgroundColor: '#e8e8e8',
                },
              }}
              onClick={() => setShowLinkAccountModal(true)}
            >
              Manage my Login Methods
            </Button>
            {/* only visable to users with email/password logins, not google login */}
            {user && user.auth0_id && user.auth0_id.startsWith('auth0|') && (
              <Button
                variant="text"
                disableRipple
                sx={{
                  textTransform: 'none',
                  borderLeft: 1.5,
                  borderRadius: 0,
                  height: '15px',
                  '&:focus': {
                    backgroundColor: '#e8e8e8',
                  },
                }}
                onClick={() => setShowResetPasswordModal(true)}
              >
                Reset my password{' '}
              </Button>
            )}
            {invitingUser && effectiveOrgUuid && user && token && (
              <OrgUserAdd
                invitingUser={invitingUser}
                setInvitingUser={setInvitingUser}
                currentUser={user}
                token={token}
                orgUuid={effectiveOrgUuid}
              />
            )}
            {token && orgUsers && orgUsers.length > 1 ? (
              <OrgUsersList
                orgUsers={orgUsers.filter(
                  (u: OrgUser) => u.uuid !== user?.uuid
                )}
                orgUuid={effectiveOrgUuid}
                token={token}
                isMobile={isMobile}
              />
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  mt: 8,
                  backgroundColor: '#f5f5f5',
                  width: '50%',
                  mx: 'auto',
                  height: '20vh',
                  borderRadius: 3,
                  border: 1,
                }}
              >
                <Typography
                  variant="h6"
                  component="h2"
                  sx={{ textAlign: 'center', mb: 1 }}
                >
                  There are no other users in your organisation
                </Typography>
                <Typography
                  variant="body1"
                  component="h2"
                  sx={{ textAlign: 'center', mb: 1 }}
                >
                  Invite a user to join your organisation with the button above
                </Typography>
              </Box>
            )}
            {invitations && invitations.length > 0 && (
              <OrgUsersInvitationsList
                invitations={invitations}
                token={token}
                orgUuid={effectiveOrgUuid}
                isMobile={isMobile}
              />
            )}
          </Box>
        </>
      </Authenticated>
    </>
  )
}
